import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { parseEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi, errorApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { entityFeedbackApiRef } from '@backstage-community/plugin-entity-feedback';
import { qetaApiRef } from '@drodil/backstage-plugin-qeta-react';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FeedbackIcon from '@material-ui/icons/Feedback';
import StarIcon from '@material-ui/icons/Star';
import StarOutlineIcon from '@material-ui/icons/StarOutline';
import useAsync from 'react-use/lib/useAsync';
import { CustomFeedbackResponseDialog } from '../entity-feedback/CustomFeedbackResponseDialog';

enum FeedbackRatings {
  one = 1,
  two = 2,
  three = 3,
  four = 4,
  five = 5,
}

const useStyles = makeStyles(theme => ({
  starRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  buttonWrapper: {
    display: 'flex',
    flexShrink: 0,
  },
}));

/**
 * "Provide Feedback" button rendered as introElement in QetaPage.
 * Visible on question/article pages. Resolves the entity ref from
 * the `?entity=` query param, or by fetching the post's entities via Qeta API.
 */
export const QetaFeedbackButton = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [dialogEntityRef, setDialogEntityRef] = useState<string>();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const qetaApi = useApi(qetaApiRef);

  const postMatch = useMemo(() => {
    const match = location.pathname.match(/\/qeta\/((?:questions|articles)\/(\d+))/);
    if (!match) return undefined;
    return { path: match[1], id: match[2] };
  }, [location.pathname]);

  const entityParam = searchParams.get('entity') ?? undefined;

  const { value: postEntities } = useAsync(async () => {
    if (!postMatch) return undefined;
    const post = await qetaApi.getPost(postMatch.id);
    return post?.entities;
  }, [postMatch?.id, qetaApi]);

  const allEntityRefs: string[] = useMemo(() => {
    if (postEntities && postEntities.length > 0) return postEntities;
    return entityParam ? [entityParam] : [];
  }, [entityParam, postEntities]);

  if (!postMatch || allEntityRefs.length === 0) {
    return null;
  }

  const getEntityName = (ref: string) => {
    try {
      return parseEntityRef(ref).name;
    } catch {
      return ref;
    }
  };

  const openDialog = (ref: string) => {
    setDialogEntityRef(ref);
    setMenuOpen(false);
  };

  return (
    <Box className={classes.buttonWrapper}>
      <ButtonGroup variant="outlined" color="primary" ref={anchorRef}>
        <Button
          startIcon={<FeedbackIcon />}
          onClick={() => setMenuOpen(prev => !prev)}
        >
          Feedback on Entity
        </Button>
        <Button
          size="small" aria-label="Select entity"
          onClick={() => setMenuOpen(prev => !prev)}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper open={menuOpen} anchorEl={anchorRef.current} transition style={{ zIndex: 1300 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <MenuList>
                  {allEntityRefs.map(ref => (
                    <MenuItem key={ref} onClick={() => openDialog(ref)}>
                      {getEntityName(ref)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {dialogEntityRef && (
        <FeedbackDialog
          entityRef={dialogEntityRef}
          qetaPostId={postMatch.path}
          open={!!dialogEntityRef}
          onClose={() => setDialogEntityRef(undefined)}
        />
      )}
    </Box>
  );
};

interface FeedbackDialogProps {
  entityRef: string;
  qetaPostId?: string;
  open: boolean;
  onClose: () => void;
}

function FeedbackDialog({ entityRef, qetaPostId, open: isOpen, onClose }: FeedbackDialogProps) {
  const classes = useStyles();
  const errorApi = useApi(errorApiRef);
  const feedbackApi = useApi(entityFeedbackApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [rating, setRating] = useState<FeedbackRatings>();
  const [isLoadingRating, setIsLoadingRating] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);

  const parsedRef = useMemo(() => {
    try {
      return parseEntityRef(entityRef);
    } catch {
      return undefined;
    }
  }, [entityRef]);

  const { value: entity } = useAsync(async () => {
    if (!parsedRef) return undefined;
    return catalogApi.getEntityByRef(entityRef);
  }, [entityRef, parsedRef, catalogApi]);

  const displayName = entity?.metadata?.title ?? parsedRef?.name ?? entityRef;

  useEffect(() => {
    if (!isOpen) return () => { };
    let cancelled = false;
    const fetchRating = async () => {
      setIsLoadingRating(true);
      try {
        const identity = await identityApi.getBackstageIdentity();
        const prevRatings = await feedbackApi.getRatings(entityRef);
        const prev = prevRatings.find(
          r => r.userRef === identity.userEntityRef,
        )?.rating;
        if (!cancelled) {
          setRating(
            prev ? (parseInt(prev, 10) as FeedbackRatings) : undefined,
          );
        }
      } catch {
        if (!cancelled) setRating(undefined);
      } finally {
        if (!cancelled) setIsLoadingRating(false);
      }
    };
    fetchRating();
    return () => { cancelled = true; };
  }, [isOpen, entityRef, feedbackApi, identityApi]);

  const applyRating = useCallback(
    async (newRating: FeedbackRatings) => {
      if (newRating === rating) {
        setIsResponseDialogOpen(true);
        return;
      }
      try {
        await feedbackApi.recordRating(entityRef, newRating.toString());
        setRating(newRating);
        setIsResponseDialogOpen(true);
      } catch (e) {
        errorApi.post(e as Error);
      }
    },
    [rating, entityRef, feedbackApi, errorApi],
  );

  return (
    <>
      <Dialog open={isOpen && (!isResponseDialogOpen || !entity)} onClose={onClose} maxWidth="xs" fullWidth>
        {isLoadingRating && <Progress />}
        <DialogTitle>Provide Feedback: {displayName}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" align="center" gutterBottom>
            Rate this entity
          </Typography>
          <Box className={classes.starRow}>
            {Object.values(FeedbackRatings)
              .filter(o => typeof o === 'number')
              .map(starRating => (
                <IconButton
                  key={starRating}
                  aria-label={`Rate ${starRating} out of 5`}
                  onClick={() => applyRating(starRating as FeedbackRatings)}
                  disabled={isLoadingRating}
                >
                  {rating && rating >= (starRating as number) ? (
                    <StarIcon fontSize="large" color="primary" />
                  ) : (
                    <StarOutlineIcon fontSize="large" />
                  )}
                </IconButton>
              ))}
          </Box>
          <Divider />
          <Box mt={1} mb={1} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              Click a star to rate, then provide detailed feedback
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {entity && isResponseDialogOpen && (
        <CustomFeedbackResponseDialog
          entity={entity}
          qetaPostId={qetaPostId}
          open={isResponseDialogOpen}
          onClose={() => {
            setIsResponseDialogOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
}